import React, { useEffect } from "react";
import { Box, Typography, Avatar, List, ListItem, Stack, TextField, InputAdornment, Select, MenuItem, IconButton, } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos, Clear } from "@mui/icons-material";
import { FaSearch } from "react-icons/fa";
import moment from "moment";
import { t } from "i18next";
import { setConnection } from "../../redux/slices/certConnectSlice";
import { toggleModal } from "../../redux/slices/modalSlice";
import { AppDispatch } from "../../redux/store";
import { useDispatch } from "react-redux";
import "../../sass/base/listView.scss";
import iconImage from "../../assets/images/user-image.png";
interface Props {
  gridRows: any[];
  handleSearch: () => void;
  totalCount: number;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: React.Dispatch<React.SetStateAction<{ page: number; pageSize: number }>>;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  onSearch: () => void;
  onRowClick: (row: any) => void;
}

export const ListViewCertifiedConnection: React.FC<Props> = ({
  gridRows,
  handleSearch,
  totalCount,
  paginationModel,
  setPaginationModel,
  searchTerm,
  setSearchTerm,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const handleMobileListRecordClick = (row: any) => {
    dispatch(
      setConnection({
        ...row,
        ContactProfilePic: row.ContactProfilePic || "",
        ContactCCOID: row.ContactCCOID || "",
        ContactName: row.FirstName + " " + row.LastName,
      })
    );
    dispatch(toggleModal("connectionModal"));
  };
  useEffect(() => {
  if (searchTerm === "") {
    handleSearch();
  }
}, [searchTerm]);

  const ACTION_REQUIRED_LABELS: Record<number, string> = {
    1: t("dataGrid.accountLocked"),
    2: t("dataGrid.pendingConnectionApproval"),
    3: t("dataGrid.noCertifications"),
    4: t("dataGrid.missingPhoto"),
  };
  const ACTION_REQUIRED_CODE_BY_LABEL: Record<string, number> = {
    "Account Locked": 1,
    "Pending Connection Approval": 2,
    "No Certifications": 3,
    "Missing Photo": 4,
  };
  const getActionRequiredValue = (v: unknown): number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    if (typeof v === "string") {
      const s = v.trim();
      if (!s) return null;
      const mapped = ACTION_REQUIRED_CODE_BY_LABEL[s];
      if (mapped) return mapped;
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }
    const n = Number(v as any);
    return Number.isFinite(n) ? n : null;
  };
  const getActionRequired = (actionRequired: number | null) => {
    if (actionRequired === null || actionRequired === undefined) return null;
    const label = ACTION_REQUIRED_LABELS[actionRequired] ?? String(actionRequired);

    if (actionRequired === 1) {
      return { label: t("dataGrid.accountLocked"), color: "#fff", backgroundColor: "#ff0000" };
    } else if (actionRequired === 2) {
      return { label: t("dataGrid.pendingConnectionApproval"), color: "#000000", backgroundColor: "#ffff00" };
    } else if (actionRequired === 3) {
      return { label: t("dataGrid.noCertifications"), color: "#fff", backgroundColor: "#9e9e9e" };
    } else if (actionRequired === 4) {
      return { label: t("dataGrid.missingPhoto"), color: "#fff", backgroundColor: "#ff0000" };
    }
    return { label, color: "#000", backgroundColor: "#eee" };
  };
  return (
    <Box className="main-box">

      <Typography className="typography">
        {t("dataGrid.ResultsCount", { count: totalCount })}
      </Typography>

      <Box className="inner-box">
        <TextField
          size="small"
          placeholder={t("dataGrid.SearchCertified")}
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
         className="text-field"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box className="input-box">
                  {searchTerm && (
                    <Clear
                      fontSize="small"
                     onClick={() => {setSearchTerm("");}}
                     className="clear-icon"                    />
                  )}
                  <FaSearch
                    className="search-icon clickable"
                    onClick={handleSearch}
                  />
                </Box>
              </InputAdornment>
            ),
          }}
        />

      </Box>


      <List sx={{ p: 0 }}>
        {gridRows.map((row) => {
          const action = getActionRequired(getActionRequiredValue((row as any).ActionRequired));

          return (
            <ListItem
              key={row.uniqueId}
              onClick={() => handleMobileListRecordClick(row)} className="list-item"  >
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap={2}>
                  <div className="certconnect-list-photo">
                    <img src={row.ContactProfilePic || iconImage} alt="profile" />
                  </div>
                  <Box>
                    <Typography fontWeight={600} color="#171725">
                      {row.ContactName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CCO ID: {row.ContactCCOID || "—"}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                      {row.MyCCODesignationDetails && (
                        <Typography variant="body2" sx={{
                          backgroundColor: "#e37e33", color: "#fff", px: 1, py: "2px",
                          borderRadius: "4px", fontSize: "0.75rem", fontWeight: 500
                        }}>
                          {row.MyCCODesignationDetails}
                        </Typography>
                      )}

                      {row.CertExpirationDate && (
                        (() => {
                          const expiryDate = moment(row.CertExpirationDate).startOf('day');
                          const today = moment().startOf('day');
                          const daysLeft = expiryDate.diff(today, 'days');
                          const isCritical = daysLeft >= 0 && daysLeft <= 30;
                          const isWarning = daysLeft > 30 && daysLeft <= 365;

                          return (
                            <Typography variant="body2" sx={{
                              px: 1, py: "2px", borderRadius: 1, fontWeight: 600, fontSize: "0.75rem",
                               backgroundColor: isCritical || isWarning ? "yellow" : "transparent", color: "#000", border: isCritical ? "2px solid red" : "none"
                            }}>
                              Exp: {moment(expiryDate).format('YYYY-MM-DD')}
                            </Typography>
                          );
                        })()
                      )}


                    </Box>
                  </Box>
                </Box>
              </Box>
            </ListItem>
          );
        })}
      </List>

      {totalCount > 0 && (
        <Stack spacing={1} alignItems="left" sx={{ mt: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
          >
            {/* Rows per page */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography >
                {t("dataGrid.Rows")}
              </Typography>
              <Select
                size="small"
                value={paginationModel.pageSize}
                onChange={(e) =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    pageSize: Number(e.target.value),
                    page: 0,
                  }))
                }
                sx={{
                  minWidth: 40,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "none" },
                  "& .MuiSelect-select": { py: 0.5, px: 1, fontSize: "0.8rem" },
                }}
              >
                {[10, 25, 50].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Current range / total */}
            <Typography variant="body2" fontWeight={600}>
              {paginationModel.page * paginationModel.pageSize + 1} –{" "}
              {Math.min(
                (paginationModel.page + 1) * paginationModel.pageSize,
                totalCount
              )}{" "}
              of {totalCount}
            </Typography>

            {/* Previous / Next arrows */}
            <IconButton
              size="small"
              disabled={paginationModel.page === 0}
              onClick={() =>
                setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))
              }
              sx={{ color: "#363534" }}
            >
              <ArrowBackIosNew fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={
                (paginationModel.page + 1) * paginationModel.pageSize >= totalCount
              }
              onClick={() =>
                setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))
              }
              sx={{ color: "#363534" }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default ListViewCertifiedConnection;
